import axios from 'axios';
import supabase from '../supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8888';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  
  return config;
});

// Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      supabase.auth.signOut();
    }
    if (error.response?.status === 403) {
      console.error('Admin access required');
    }
    return Promise.reject(error);
  }
);

// Admin API endpoints
export const adminApi = {
  // Users
  getUsers: (params) => api.get('/admin/api/users', { params }),
  getUser: (userId) => api.get(`/admin/api/users/${userId}`),
  updateUserRole: (userId, role) => api.patch(`/admin/api/users/${userId}/role`, { role }),
  toggleUserStatus: (userId, disabled) => api.patch(`/admin/api/users/${userId}/status`, { disabled }),
  
  // Recipes
  getRecipes: (params) => api.get('/admin/api/recipes', { params }),
  getRecipe: (recipeId) => api.get(`/admin/api/recipes/${recipeId}`),
  updateRecipe: (recipeId, data) => api.patch(`/admin/api/recipes/${recipeId}`, data),
  deleteRecipe: (recipeId) => api.delete(`/admin/api/recipes/${recipeId}`),
  bulkDeleteRecipes: (recipeIds) => api.post('/admin/api/recipes/bulk-delete', { recipeIds }),
  uploadRecipeImage: (recipeId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/admin/api/recipes/${recipeId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteRecipeImage: (recipeId) => api.delete(`/admin/api/recipes/${recipeId}/image`),
  generateRecipeImage: (recipeId) => api.post(`/admin/api/recipes/${recipeId}/generate-image`),
  
  // Books
  getBooks: (params) => api.get('/admin/api/books', { params }),
  getBook: (bookId) => api.get(`/admin/api/books/${bookId}`),
  updateBook: (bookId, data) => api.patch(`/admin/api/books/${bookId}`, data),
  deleteBook: (bookId) => api.delete(`/admin/api/books/${bookId}`),
  mergeBooks: (sourceId, targetId) => api.post('/admin/api/books/merge', { sourceId, targetId }),
  
  // Tags
  getTags: (params) => api.get('/admin/api/tags', { params }),
  createTag: (data) => api.post('/admin/api/tags', data),
  updateTag: (tagId, data) => api.patch(`/admin/api/tags/${tagId}`, data),
  deleteTag: (tagId) => api.delete(`/admin/api/tags/${tagId}`),
  
  // Tag Categories
  getTagCategories: () => api.get('/admin/api/tag-categories'),
  createTagCategory: (data) => api.post('/admin/api/tag-categories', data),
  updateTagCategory: (categoryId, data) => api.patch(`/admin/api/tag-categories/${categoryId}`, data),
  deleteTagCategory: (categoryId) => api.delete(`/admin/api/tag-categories/${categoryId}`),
  
  // Analytics
  getDashboardStats: () => api.get('/admin/api/analytics/dashboard'),
  getUserGrowth: (period) => api.get('/admin/api/analytics/user-growth', { params: { period } }),
  getRecipeStats: (period) => api.get('/admin/api/analytics/recipe-stats', { params: { period } }),
  getSearchAnalytics: (period) => api.get('/admin/api/analytics/search', { params: { period } }),
  
  // Cache
  getCacheStats: () => api.get('/admin/api/cache/stats'),
  getCacheEntries: (params) => api.get('/admin/api/cache/entries', { params }),
  clearCacheEntry: (url) => api.delete('/admin/api/cache/entry', { params: { url } }),
  clearAllCache: () => api.delete('/admin/api/cache/all'),
  getQualityStats: () => api.get('/admin/api/cache/quality-stats'),
  getDomainAnalytics: (params) => api.get('/admin/api/cache/domain-analytics', { params }),
  getUrlFailures: () => api.get('/admin/api/cache/url-failures'),
  clearExpiredFailures: () => api.post('/admin/api/cache/clear-expired-failures'),
  
  // System / Queues
  getQueueStats: () => api.get('/admin/api/system/queues'),
  retryFailedJobs: (queueName) => api.post(`/admin/api/system/queues/${queueName}/retry-failed`),
  clearCompletedJobs: (queueName) => api.post(`/admin/api/system/queues/${queueName}/clear-completed`),
  triggerReindex: () => api.post('/admin/api/system/reindex'),
  getSystemHealth: () => api.get('/admin/api/system/health'),
};

export default api;
